// Dual mood client: ritual check-in + weather + evidence (why + entry sources).

import { apiGet, apiPost } from './client';
import { openCipher } from './cipher';
import { sealWithKey } from '../crypto/crypto-vault';

export const MOOD_OPTIONS = [
  { key: 'heavy', label: 'Heavy' },
  { key: 'raw', label: 'Raw' },
  { key: 'restless', label: 'Restless' },
  { key: 'tender', label: 'Tender' },
  { key: 'steady', label: 'Steady' },
  { key: 'calm', label: 'Calm' },
  { key: 'bright', label: 'Bright' },
] as const;

export type MoodKey = (typeof MOOD_OPTIONS)[number]['key'];
export type EnergyKey = 'drained' | 'steady' | 'wired';
export type BodyKey = 'tight' | 'open' | 'numb' | 'restless';

export const ENERGY_OPTIONS: { key: EnergyKey; label: string }[] = [
  { key: 'drained', label: 'Drained' },
  { key: 'steady', label: 'Steady energy' },
  { key: 'wired', label: 'Wired' },
];

export const BODY_OPTIONS: { key: BodyKey; label: string }[] = [
  { key: 'tight', label: 'Tight' },
  { key: 'open', label: 'Open' },
  { key: 'numb', label: 'Numb' },
  { key: 'restless', label: 'Restless body' },
];

/** Slider bands → display name + description for the ritual. */
export const VALENCE_BANDS: { max: number; name: string; blurb: string; hero: MoodHeroId }[] = [
  { max: -0.65, name: 'Heavy', blurb: 'You feel weighed down, dense, hard to lift.', hero: 'heavy' },
  { max: -0.4, name: 'Sad', blurb: 'You feel heavy, tender, and emotionally low.', hero: 'sad' },
  { max: -0.15, name: 'Tired', blurb: 'You feel drained, sleepy, and low on energy.', hero: 'tired' },
  { max: 0.1, name: 'Tender', blurb: 'You feel soft, open, a little raw at the edges.', hero: 'sad' },
  { max: 0.35, name: 'Steady', blurb: 'You feel present — neither lifted nor sunk.', hero: 'steady' },
  { max: 0.6, name: 'Calm', blurb: 'You feel quieter, clearer, more at ease.', hero: 'calm' },
  { max: 1.01, name: 'Bright', blurb: 'You feel lighter, warmer, more alive.', hero: 'bright' },
];

export type MoodHeroId = 'heavy' | 'tired' | 'sad' | 'steady' | 'calm' | 'bright';

export function bandForValence(v: number) {
  for (const b of VALENCE_BANDS) {
    if (v <= b.max) return b;
  }
  return VALENCE_BANDS[VALENCE_BANDS.length - 1];
}

export function moodKeyFromValence(v: number): MoodKey {
  if (v <= -0.65) return 'heavy';
  if (v <= -0.4) return 'raw';
  if (v <= -0.15) return 'restless';
  if (v < 0.1) return 'tender';
  if (v < 0.35) return 'steady';
  if (v < 0.6) return 'calm';
  return 'bright';
}

export interface MoodCheckIn {
  day: string;
  moodKey: MoodKey;
  valence: number;
  energyKey: EnergyKey | null;
  bodyKey: BodyKey | null;
  noteCipher: string | null;
  createdAt: string;
}

export interface DayWeather {
  day: string;
  selfMood: MoodKey | null;
  selfValence: number | null;
  journalValence: number | null;
  sampleCount: number;
  topEmotions: string[];
}

export interface DissonanceCard {
  present: boolean;
  selfMood: MoodKey | null;
  selfValence: number | null;
  journalValence: number | null;
  sampleCount: number;
  message: string | null;
}

export interface RisingSignal {
  kind: 'emotion' | 'theme' | 'body' | 'symbol' | string;
  label: string;
  canonicalId: string;
  count: number;
  trajectory: string | null;
}

export interface MoodWeather {
  days: number;
  today: string;
  weather: DayWeather[];
  dissonance: DissonanceCard;
  risingSignals: RisingSignal[];
  journalNetValence: number | null;
  journalSampleCount: number;
}

export interface MoodEvidenceSource {
  entryId: string;
  detectedAt: string;
  surfaceForm: string;
  snippet: string;
  valence: number | null;
}

export interface MoodEvidenceClaim {
  canonicalId: string;
  label: string;
  occurrenceCount: number;
  avgValence: number | null;
  why: string;
  sources: MoodEvidenceSource[];
}

export interface MoodEvidence {
  days: number;
  claims: MoodEvidenceClaim[];
  dissonance: DissonanceCard & { sources: MoodEvidenceSource[] };
}

function asMoodKey(raw: string | null | undefined): MoodKey | null {
  if (!raw) return null;
  return MOOD_OPTIONS.some((o) => o.key === raw) ? (raw as MoodKey) : null;
}

export async function postMoodCheckIn(args: {
  valence: number;
  moodKey?: MoodKey;
  energyKey?: EnergyKey | null;
  bodyKey?: BodyKey | null;
  note?: string | null;
  dek?: CryptoKey | null;
  day?: string;
}): Promise<MoodCheckIn> {
  let note_cipher: string | null = null;
  if (args.note && args.note.trim() && args.dek) {
    note_cipher = await sealWithKey(args.dek, args.note.trim().slice(0, 2000));
  }

  const res = await apiPost<{
    day: string;
    mood_key: string;
    valence: number;
    energy_key?: string | null;
    body_key?: string | null;
    created_at: string;
  }>('/api/mood/check-in', {
    valence: args.valence,
    mood_key: args.moodKey ?? moodKeyFromValence(args.valence),
    energy_key: args.energyKey ?? null,
    body_key: args.bodyKey ?? null,
    note_cipher,
    ...(args.day ? { day: args.day } : {}),
  });

  return {
    day: res.day,
    moodKey: (asMoodKey(res.mood_key) ?? moodKeyFromValence(args.valence)) as MoodKey,
    valence: Number(res.valence ?? args.valence),
    energyKey: (res.energy_key as EnergyKey) ?? null,
    bodyKey: (res.body_key as BodyKey) ?? null,
    noteCipher: note_cipher,
    createdAt: res.created_at,
  };
}

export async function fetchMoodWeather(
  days = 14,
  signal?: AbortSignal,
): Promise<MoodWeather> {
  const res = await apiGet<{
    days?: number;
    today?: string;
    weather?: Array<{
      day: string;
      selfMood?: string | null;
      selfValence?: number | null;
      journalValence?: number | null;
      sampleCount?: number;
      topEmotions?: string[];
    }>;
    dissonance?: {
      present?: boolean;
      selfMood?: string | null;
      selfValence?: number | null;
      journalValence?: number | null;
      sampleCount?: number;
      message?: string | null;
    };
    rising_signals?: Array<{
      kind?: string;
      label?: string;
      canonicalId?: string;
      count?: number;
      trajectory?: string | null;
    }>;
    journal_net_valence?: number | null;
    journal_sample_count?: number;
  }>(`/api/mood/weather?days=${days}`, signal);

  const dissonanceRaw = res.dissonance ?? {};
  return {
    days: Number(res.days ?? days),
    today: res.today ?? '',
    weather: (res.weather ?? []).map((d) => ({
      day: d.day,
      selfMood: asMoodKey(d.selfMood ?? null),
      selfValence: d.selfValence == null ? null : Number(d.selfValence),
      journalValence: d.journalValence == null ? null : Number(d.journalValence),
      sampleCount: Number(d.sampleCount ?? 0),
      topEmotions: d.topEmotions ?? [],
    })),
    dissonance: {
      present: Boolean(dissonanceRaw.present),
      selfMood: asMoodKey(dissonanceRaw.selfMood ?? null),
      selfValence: dissonanceRaw.selfValence == null ? null : Number(dissonanceRaw.selfValence),
      journalValence:
        dissonanceRaw.journalValence == null ? null : Number(dissonanceRaw.journalValence),
      sampleCount: Number(dissonanceRaw.sampleCount ?? 0),
      message: dissonanceRaw.message ?? null,
    },
    risingSignals: (res.rising_signals ?? []).map((s) => ({
      kind: s.kind ?? 'theme',
      label: s.label ?? '',
      canonicalId: s.canonicalId ?? '',
      count: Number(s.count ?? 0),
      trajectory: s.trajectory ?? null,
    })),
    journalNetValence: res.journal_net_valence == null ? null : Number(res.journal_net_valence),
    journalSampleCount: Number(res.journal_sample_count ?? 0),
  };
}

async function mapSources(
  dek: CryptoKey,
  raw: Array<{
    entry_id?: string;
    detected_at?: string;
    surface_form?: string;
    context_cipher?: string | null;
    valence?: number | null;
  }>,
): Promise<MoodEvidenceSource[]> {
  return Promise.all(
    raw.map(async (s) => ({
      entryId: s.entry_id ?? '',
      detectedAt: s.detected_at ?? '',
      surfaceForm: s.surface_form ?? '',
      snippet: await openCipher(dek, s.context_cipher),
      valence: s.valence == null ? null : Number(s.valence),
    })),
  );
}

export async function fetchMoodEvidence(
  dek: CryptoKey,
  days = 14,
  signal?: AbortSignal,
): Promise<MoodEvidence> {
  const res = await apiGet<{
    days?: number;
    claims?: Array<{
      canonical_id?: string;
      label?: string;
      occurrence_count?: number;
      avg_valence?: number | null;
      why?: string;
      sources?: Array<{
        entry_id?: string;
        detected_at?: string;
        surface_form?: string;
        context_cipher?: string | null;
        valence?: number | null;
      }>;
    }>;
    dissonance?: {
      present?: boolean;
      selfMood?: string | null;
      selfValence?: number | null;
      journalValence?: number | null;
      sampleCount?: number;
      message?: string | null;
      sources?: Array<{
        entry_id?: string;
        detected_at?: string;
        surface_form?: string;
        context_cipher?: string | null;
        valence?: number | null;
      }>;
    };
  }>(`/api/mood/evidence?days=${days}`, signal);

  const claims = await Promise.all(
    (res.claims ?? []).map(async (c) => ({
      canonicalId: c.canonical_id ?? '',
      label: c.label ?? '',
      occurrenceCount: Number(c.occurrence_count ?? 0),
      avgValence: c.avg_valence == null ? null : Number(c.avg_valence),
      why: c.why ?? '',
      sources: await mapSources(dek, c.sources ?? []),
    })),
  );

  const d = res.dissonance ?? {};
  return {
    days: Number(res.days ?? days),
    claims: claims.filter((c) => c.canonicalId && c.sources.length > 0),
    dissonance: {
      present: Boolean(d.present),
      selfMood: asMoodKey(d.selfMood ?? null),
      selfValence: d.selfValence == null ? null : Number(d.selfValence),
      journalValence: d.journalValence == null ? null : Number(d.journalValence),
      sampleCount: Number(d.sampleCount ?? 0),
      message: d.message ?? null,
      sources: await mapSources(dek, d.sources ?? []),
    },
  };
}
